// src/hooks/usePGTOperations.js
import { useState, useCallback, useMemo } from 'react';
import { BrowserProvider, Contract } from 'ethers';
import PGT_ABI from '../abis/PGTToken.json';
import EventTicketABI from '../abis/EventTicket.json';

/* eslint-disable no-undef */
const _BigInt = typeof globalThis.BigInt !== 'undefined' ? globalThis.BigInt : (v) => Number(v);
/* eslint-enable no-undef */
const pow10 = (d) => _BigInt(10) ** _BigInt(d);

const PGT_ADDRESS = process.env.REACT_APP_PGT_ADDRESS;
const EVENT_TICKET_ADDRESS = process.env.REACT_APP_EVENT_TICKET_ADDRESS;
const SIMPLE_SWAP_ADDRESS = process.env.REACT_APP_SIMPLE_SWAP_ADDRESS;

const isPseudoETH = (addr) =>
  addr && addr.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';

/**
 * Custom hook encapsulating PGT balance, allowance, approval, and token purchase logic.
 */
export default function usePGTOperations({ account, isActive, chainId, showNotification }) {
  const [pgtBalance, setPgtBalance] = useState(0n);
  const [pgtDecimals, setPgtDecimals] = useState(2); // PGT has 2 decimals
  const [allowance, setAllowance] = useState(0n);
  const [approving, setApproving] = useState(false);
  const [buyUSD, setBuyUSD] = useState(10);
  const [buyingPGT, setBuyingPGT] = useState(false);
  const [supportedTokens, setSupportedTokens] = useState([]);
  const [showBuyModal, setShowBuyModal] = useState(false);

  // Read-only provider
  const readOnlyProvider = useMemo(() => {
    if (typeof window === 'undefined' || !window.ethereum) return null;
    return new BrowserProvider(window.ethereum);
  }, []);

  // Read-only contracts
  const pgtContractRO = useMemo(() => {
    if (!readOnlyProvider || !PGT_ADDRESS) return null;
    return new Contract(PGT_ADDRESS, PGT_ABI, readOnlyProvider);
  }, [readOnlyProvider]);

  const eventTicketRO = useMemo(() => {
    if (!readOnlyProvider || !EVENT_TICKET_ADDRESS) return null;
    return new Contract(EVENT_TICKET_ADDRESS, EventTicketABI, readOnlyProvider);
  }, [readOnlyProvider]);

  // Refresh allowance
  const refreshAllowance = useCallback(async () => {
    if (!account || !pgtContractRO || !eventTicketRO) return 0n;
    try {
      const spender = await eventTicketRO.getAddress();
      const value = await pgtContractRO.allowance(account, spender);
      const newAllowance = _BigInt(value.toString());
      setAllowance(newAllowance);
      return newAllowance;
    } catch (e) {
      console.warn('allowance load failed', e);
      return 0n;
    }
  }, [account, pgtContractRO, eventTicketRO]);

  // Refresh balance
  const refreshBalance = useCallback(async () => {
    if (!account || !pgtContractRO) return;
    try {
      const balance = await pgtContractRO.balanceOf(account);
      setPgtBalance(_BigInt(balance.toString()));
    } catch (e) {
      console.warn('balance load failed', e);
    }
  }, [account, pgtContractRO]);

  // Approve PGT spending
  const approvePGT = useCallback(
    async (requiredBN) => {
      if (!isActive || !account) {
        showNotification('error', 'Connect wallet first');
        return false;
      }
      if (!PGT_ADDRESS || !EVENT_TICKET_ADDRESS) {
        showNotification('error', 'Contract addresses not configured');
        return false;
      }

      setApproving(true);
      try {
        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const pgtContract = new Contract(PGT_ADDRESS, PGT_ABI, signer);
        const eventTicket = new Contract(EVENT_TICKET_ADDRESS, EventTicketABI, provider);

        const spender = await eventTicket.getAddress();
        const tx = await pgtContract.approve(spender, requiredBN);
        const receipt = await tx.wait();

        await new Promise(resolve => setTimeout(resolve, 1000));
        await refreshAllowance();

        showNotification('success', 'PGT approval successful! You can now purchase tickets.', receipt.hash);
        return true;
      } catch (e) {
        console.error('approve failed', e);
        showNotification('error', `Approval failed: ${e?.reason || e?.message || 'Unknown error'}`);
        return false;
      } finally {
        setApproving(false);
      }
    },
    [isActive, account, refreshAllowance, showNotification]
  );

  // Ensure allowance is sufficient
  const ensureAllowanceForAmount = useCallback(
    async (requiredBN, autoApprove = false) => {
      if (allowance >= requiredBN) return true;
      if (!autoApprove) return false;
      return await approvePGT(requiredBN);
    },
    [allowance, approvePGT]
  );

  // Buy PGT with token
  const buyPGTWithToken = useCallback(
    async (token) => {
      if (!window.ethereum || !isActive || !SIMPLE_SWAP_ADDRESS) {
        showNotification('error', 'Wallet or SimpleSwap not ready');
        return;
      }
      setBuyingPGT(true);
      try {
        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();

        const swap = new Contract(
          SIMPLE_SWAP_ADDRESS,
          [
            'function tokens(address) view returns (bool enabled, uint8 decimals, uint256 usdRate)',
            'function buyPGT(address payToken, uint256 amountIn) external',
          ],
          signer
        );

        const [enabled, tokenDecU8, usdRateRaw] = await swap.tokens(token.address);
        const tokenDecimals = Number(tokenDecU8);
        const usdRate = typeof usdRateRaw === 'bigint' ? usdRateRaw : _BigInt(usdRateRaw?.toString?.() || '0');

        if (!enabled || usdRate <= 0n) {
          showNotification('error', `${token.symbol} is not available for PGT purchase.`);
          setBuyingPGT(false);
          return;
        }

        const { parseUnits } = await import('ethers');
        const desiredBN = parseUnits(String(buyUSD), pgtDecimals);
        const amountIn = (desiredBN * pow10(tokenDecimals)) / usdRate;
        if (amountIn <= 0n) {
          showNotification('error', 'Amount too small. Increase USD amount.');
          setBuyingPGT(false);
          return;
        }

        const erc20 = new Contract(
          token.address,
          [
            'function balanceOf(address) view returns (uint256)',
            'function allowance(address owner, address spender) view returns (uint256)',
            'function approve(address spender, uint256 amount) external returns (bool)',
          ],
          signer
        );
        const me = await signer.getAddress();
        const bal = await erc20.balanceOf(me);
        if (bal < amountIn) {
          showNotification('error', `Insufficient ${token.symbol} balance.`);
          setBuyingPGT(false);
          return;
        }

        if (pgtContractRO) {
          const pool = await pgtContractRO.balanceOf(SIMPLE_SWAP_ADDRESS);
          if (_BigInt(pool.toString()) < desiredBN) {
            showNotification('error', 'Not enough PGT in liquidity pool.');
            setBuyingPGT(false);
            return;
          }
        }

        const allowanceSwap = await erc20.allowance(me, SIMPLE_SWAP_ADDRESS);
        if (allowanceSwap < amountIn) {
          const txA = await erc20.approve(SIMPLE_SWAP_ADDRESS, amountIn);
          await txA.wait();
        }

        const tx = await swap.buyPGT(token.address, amountIn);
        await tx.wait();

        if (pgtContractRO) {
          const balance = await pgtContractRO.balanceOf(me);
          setPgtBalance(_BigInt(balance.toString()));
        }

        showNotification('success', `Bought ${buyUSD} PGT with ${token.symbol}`);
      } catch (err) {
        console.error('PGT buy failed:', err);
        showNotification('error', err?.reason || err?.message || 'Transaction failed');
      } finally {
        setBuyingPGT(false);
      }
    },
    [isActive, buyUSD, pgtDecimals, pgtContractRO, showNotification]
  );

  // Load supported tokens
  const loadSupportedTokens = useCallback(
    async (eventChainId) => {
      try {
        const networkKey = eventChainId === 8453 ? 'baseMainnet' : 'baseSepolia';
        const res = await fetch('/tokens.json');
        const config = await res.json();
        let tokens = config[networkKey]?.filter((t) => t.enabled) || [];
        tokens = tokens.filter((t) => !t.isNative && !isPseudoETH(t.address || ''));
        setSupportedTokens(tokens);
      } catch (err) {
        console.warn('Failed to load tokens.json', err);
        setSupportedTokens([]);
      }
    },
    []
  );

  return {
    // State
    pgtBalance, setPgtBalance,
    pgtDecimals, setPgtDecimals,
    allowance, setAllowance,
    approving,
    buyUSD, setBuyUSD,
    buyingPGT,
    supportedTokens,
    showBuyModal, setShowBuyModal,
    // Contracts
    pgtContractRO, eventTicketRO,
    // Actions
    refreshAllowance, refreshBalance,
    approvePGT, ensureAllowanceForAmount,
    buyPGTWithToken, loadSupportedTokens,
  };
}
